<script lang="ts" setup>
  import {
    ref,
    reactive,
    provide,
    onMounted,
    onBeforeUnmount,
    computed,
    useTemplateRef,
    watchEffect,
    watch
  } from 'vue'
  import GeneratorHistory from './components/GeneratorHistory.vue'
  import CheckedLists from './components/CheckedLists.vue'
  import UpdataLocalImage from '../AppUploadLocalImage/index.vue'
  import type { Image } from '@/types/image'
  import type { ImageItem } from '../AppUploadLocalImage/index.vue'
  import _fetch from '@/utils/fetch'
  import AppLoading from '@/components/AppLoading.vue'
  import MessageService from '@/components/AppMessage/message'
  import ToastService from '@/components/AppToast/toast'
  import { lockScroll, unlockScroll } from '@/utils/scrollLock'
  import trackEvent from '@/track/trackEvent'
  import { getCurrentPageName } from '@/utils/common'
  import creatorBannerImg from '@/assets/images/about/creator_banner_img.png'
  import { useUserStore } from '@zaohaowu/website-common/stores/userStore'
  import AppImage from '@/components/AppImage.vue'
  import getParamsFromUrl from '@/utils/shareSource'
  import getUserInfo from '@/utils/getUserInfo'
  import { toRaw } from 'vue'
  import trackUtil from '@/track/other'
  import { getDraft, saveDraft, deleteDraft } from '@/utils/draftApi'
  import { useGlobalConfigStore } from '@/stores/globalConfig'
  import http from '@/http'

  interface SelectTopicParams {
    topicId: string
    title: string
    equleNum: number
    want: number
    key?: string
    canPost?: boolean
    activityType: number
    allowPostWantByTopicIds: []
  }

  interface ConfigSettings {
    associateOriginalImage: boolean //是否关联原图
  }
  import { filterInvalidMentionList } from '@/utils/filterInvalidMention'
  import type { PostDialogConfig } from '@/hooks/usePostDialog'
  import { useSallyActivityDialog } from '@/hooks/useSallyActivityDialog'
  import type { RelatedWorkItem } from '@/types/relatedWork'
  import RelatedWorkSelectPanel from '@/components/RelatedWorkSelect/RelatedWorkSelectPanel.vue'
  import RelatedWorkSelectTipPopup from '@/components/RelatedWorkSelect/RelatedWorkSelectTipPopup.vue'
  import { STORAGE_KEYS } from '@/components/RelatedWorkSelect/constants'

  interface postChallenge {
    title: string
    description: string
    file?: File | null
    checkedImages: Map<string, Image>
    topicId: string
    topicTitle: string
    associateOriginalImage?: boolean
  }

  interface PromptResultInstance extends HTMLDivElement {
    triggerLoading(flag: boolean): void
    clearChecked(): void
  }

  enum PostStep {
    SELECT_MEDIA = 1,
    EDIT_POST = 2
  }

  interface Challenge {
    title?: string
    uniqueTopicId?: string
    banners?: { id: string; image: string; sidebarImage: string }[]
  }

  const props = defineProps<PostDialogConfig>()

  const pageName = getCurrentPageName()
  const emit = defineEmits(['close', 'success'])
  const challengeId = ref(props.challengeId)
  const hasHistory = props.hasHistory
  const checkedLength = props.checkedLength
  const wantable = ref(props.wantable)
  const tabType = ref<'history' | 'loacl'>(props.tabType || 'history')
  const step = ref<PostStep>(PostStep.SELECT_MEDIA)
  const resultRef = ref<PromptResultInstance | null>(null)
  const uploadRef = ref<any>(null)
  const percent = ref(1)
  const activitType = ref(0)
  const uploadLoading = ref(false)
  const checkedListsRef = useTemplateRef<InstanceType<typeof CheckedLists>>('checkedListsRef')
  const sourceImgInfo = ref()
  const checkCanPost = ref(false)
  const isWantableLocalUpload = computed(() => !!props.isWantableLocalUpload)
  const selectedRelatedWork = ref<RelatedWorkItem | null>(null)
  const relatedWorkPickerVisible = ref(false)
  const relatedWorkRuleTipVisible = ref(false)

  const openRelatedWorkPicker = () => {
    relatedWorkPickerVisible.value = true
    if (!localStorage.getItem(STORAGE_KEYS.RELATED_WORK_RULE_TIP_SHOWN)) {
      relatedWorkRuleTipVisible.value = true
    }
  }

  const closeRelatedWorkPicker = () => {
    relatedWorkPickerVisible.value = false
  }

  const handleRelatedWorkConfirm = (work: RelatedWorkItem | null) => {
    selectedRelatedWork.value = work
    relatedWorkPickerVisible.value = false
  }

  const handleRelatedWorkRuleTipClose = () => {
    relatedWorkRuleTipVisible.value = false
    localStorage.setItem(STORAGE_KEYS.RELATED_WORK_RULE_TIP_SHOWN, '1')
  }
  const showLocalUpload = computed(
    () =>
      props.scene === 'challenge' || hasLocalUploadPermission.value || isWantableLocalUpload.value
  )

  provide('challengeId', challengeId)
  const $message = MessageService
  const $toast = ToastService
  provide('$toast', $toast) // 提供 toast 服务给子组件
  provide('hasHistory', hasHistory)
  provide('checkedMaxLength', checkedLength)

  const postData: postChallenge = reactive({
    title: '',
    description: '',
    file: null,
    checkedImages: new Map(),
    topicId: '',
    topicTitle: ''
  })

  export interface PostDraftPayload {
    title?: string
    description?: string
    mediaList: Image[]
    selectedTopic?: { uniqueTopicId?: string; name?: string; title?: string } | null
    selectedRelatedWork?: RelatedWorkItem | null
    originalImage?: boolean
    enablePurchase?: boolean
    tabType?: 'history' | 'loacl'
    historyTabType?: 'image' | 'video'
    draftId?: string
  }

  const globalConfigStore = useGlobalConfigStore()
  const restoredFromDraft = ref(false)
  const currentDraftId = ref<string | undefined>(undefined)
  const currentDraftVersion = ref<number | undefined>(undefined)
  const historyTabType = ref<'image' | 'video'>('image')

  // 如果是在全局配置指定的挑战活动下发布，那么不存也不取草稿内容
  const isDraftDisabled = computed(() => {
    const disableIds = globalConfigStore.disableDraftChallengeIds
    if (!disableIds || !disableIds.length) return false
    const currentId = props.challengeId || postData.topicId
    return !!(currentId && disableIds.includes(currentId))
  })

  const isExitContentEmpty = () => {
    const hasImages = postData.checkedImages.size > 0
    const hasTitle = !!postData.title?.trim()
    const hasDesc = !!postData.description?.trim()
    const hasTopic = !!postData.topicId
    const hasRelatedWork = !!selectedRelatedWork.value
    const isUploading = uploadLoading.value
    return !hasImages && !hasTitle && !hasDesc && !hasTopic && !hasRelatedWork && !isUploading
  }

  const saveAsDraft = async () => {
    const imageList: Image[] = []
    postData.checkedImages.forEach(img => {
      imageList.push(toRaw(img))
    })
    const currentEnablePurchase = checkedListsRef.value?.getEnablePurchase()
    const payload: PostDraftPayload = {
      title: postData.title || '',
      description: postData.description || '',
      mediaList: imageList,
      selectedTopic: postData.topicId
        ? { uniqueTopicId: postData.topicId, name: postData.topicTitle || '' }
        : null,
      selectedRelatedWork: selectedRelatedWork.value ? toRaw(selectedRelatedWork.value) : null,
      originalImage: postData.associateOriginalImage ?? false,
      enablePurchase: typeof currentEnablePurchase === 'boolean' ? currentEnablePurchase : true,
      tabType: tabType.value,
      historyTabType: historyTabType.value,
      ...(currentDraftId.value ? { draftId: currentDraftId.value } : {})
    }
    const res = await saveDraft('POST', payload)
    restoredFromDraft.value = true
    if (res && res.draftId) {
      currentDraftId.value = res.draftId
    }
    if (res && res.version) {
      currentDraftVersion.value = res.version
    }
  }

  // 根据数据是否包含视频来判断当前发布类型
  const postType = computed(() => {
    if (postData.checkedImages && typeof postData.checkedImages.forEach === 'function') {
      let hasVideo = false
      postData.checkedImages.forEach((item: any) => {
        if (item && item.target === 'video') {
          hasVideo = true
        }
      })
      return hasVideo ? 'video' : 'image'
    }
    return 'image'
  })

  const userStore = useUserStore()
  // 关联作品入口：跟随用户信息下发的 canPublishReferralContent
  const isRelatedWorkEnabled = computed(() => !!userStore.user?.canPublishReferralContent)

  watch(isRelatedWorkEnabled, enabled => {
    if (!enabled) {
      selectedRelatedWork.value = null
      relatedWorkPickerVisible.value = false
    }
  })

  let isPGCUser =
    userStore.isCreator() || userStore.user?.showPostLocalUpload === true
      ? true
      : getParamsFromUrl('userType') === 'creator'
        ? true
        : false

  // 检查用户是否有本地上传权限
  const hasLocalUploadPermission = computed(() => {
    return userStore.user?.showPostLocalUpload === true
  })

  const showPromptSetting = ref(false)

  const initView = () => {
    if (props.scene === 'challenge' || props.scene === 'normalChallenge') {
      tabType.value = 'loacl'
    } else {
      tabType.value = props.tabType || 'history'
    }

    if (props.checkedImages?.length) {
      wantable.value = props.checkedImages[0].wantableTopics.length > 0
      step.value = PostStep.EDIT_POST
      if (props.postType === 'video') {
        const video = props.checkedImages[0]
        postData.checkedImages.set(video.url, toRaw(video))
        postData.checkedImages.set(
          getOriginImagePath(toRaw(video)),
          getOriginImageInfo(toRaw(video))
        )
      } else {
        props.checkedImages.forEach((item: Image) => {
          postData.checkedImages.set(item.url, item)
        })
      }
      // 本地上传 tab 下需要回填上传列表，否则从第二步返回时已选图不再回显
      if (tabType.value === 'loacl') {
        syncLocalImageListFromChecked()
      }
    }
  }

  const onInput = (obj: { type: string; value: string }) => {
    if (obj.type === 'title') {
      postData.title = obj.value
    } else {
      postData.description = obj.value
    }
  }

  const onCheckedMore = () => {
    step.value = PostStep.SELECT_MEDIA
  }

  const onBack = () => {
    if (step.value === PostStep.EDIT_POST) {
      confirmSaveDraft(() => {
        step.value = PostStep.SELECT_MEDIA
      }, '返回编辑')
    } else {
      step.value = PostStep.SELECT_MEDIA
    }
  }

  const nextStep = () => {
    if (postData.checkedImages.size) {
      step.value = PostStep.EDIT_POST
    }
  }
  const suiting = ref(false)

  const getPostApiUrl = (): string => {
    return '/aigc/api/topic/content/general' //这个接口可以不传topicId,后端会给一个默认值
  }

  const { isSallyTopic, showSallyActivityDialog } = useSallyActivityDialog()

  // 发布前置校验
  const postPreCheck = async () => {
    if (tabType.value === 'history') {
      if (disableBtn.value) return
    }
    // Sally活动拦截：弹出提示，不关闭发布弹窗，用户可换话题继续发布
    const currentTopicId = props.challengeId || postData.topicId
    if (isSallyTopic(currentTopicId)) {
      showSallyActivityDialog()
      return
    }
    const items = Array.from(postData.checkedImages.values())
    const hasIpProblem = items.some(
      item => item?.videoExtraInfo?.ipReviewResult || item?.imageExtraInfo?.ipReviewResult
    )
    if (hasIpProblem) {
      trackUtil.ipRemindPopShow()
      $message &&
        $message({
          type: 'error',
          title: '系统提示',
          message: '您提交的作品可能不符合商用规范，存在无法正常制作/上架的情形，是否要进行修改？',
          timer: 0,
          config: {
            showClose: false,
            showIcon: false,
            showCancel: true,
            showConfirm: true,
            confirmText: '去修改',
            cancelText: '继续发布'
          },
          onConfirm() {
            // IP风险提示，去修改
            trackUtil.ipRemindPopModify()
          },
          onCancel() {
            // IP风险提示，继续发布
            trackUtil.ipRemindPopContinue()
            postStep()
          }
        })
    } else {
      // 无IP风险
      postStep()
    }
  }

  const postStep = async () => {
    let type = isWantableLocalUpload.value || tabType.value === 'loacl' ? 2 : 1
    suiting.value = true
    const valuesArray = Array.from(postData.checkedImages.values())
    const firstImage = valuesArray.splice(0, 1)
    // 本地图片生成的视频
    const videoLocalImageUrl =
      postType.value === 'video' &&
      postData.associateOriginalImage &&
      !firstImage[0].sourceImageInfo?.itemId
        ? firstImage[0].firstFrameUrl
        : undefined

    const _mentionList = checkedListsRef.value?.getMentionList() ?? []
    const mentionList = filterInvalidMentionList(_mentionList, postData.description)
    const hidePrompt = checkedListsRef.value?.getHidePrompt() ? 1 : 0
    const topicId = isWantableLocalUpload.value
      ? props.challengeId
      : props.challengeId || postData.topicId
    const showCustomizeBuyButton = checkedListsRef.value?.getEnablePurchase() === false ? 0 : 1
    const originContentId = selectedRelatedWork.value?.uniqueContentId
    const response = await _fetch(getPostApiUrl(), {
      method: 'POST',
      body: JSON.stringify({
        topicId,
        title: postData.title,
        description: postData.description,
        posterImage: firstImage,
        contentImage: valuesArray,
        hiddenPrompt: hidePrompt,
        showCustomizeBuyButton,
        videoLocalImageUrl,
        type: type,
        ...(currentDraftId.value ? { draftId: currentDraftId.value } : {}),
        ...(currentDraftVersion.value ? { draftVersion: currentDraftVersion.value } : {}),
        mentionUser: mentionList.map(item => ({
          userId: item.userId,
          startIndex: item.startIndex,
          endIndex: item.startIndex + item.length
        })),
        ...(originContentId ? { originContentId } : {})
      })
    })
    let res = await response.json()
    suiting.value = false
    if (res.code === 200) {
      const activeTopic = challenges.value
        ? challenges.value.filter(item => item.uniqueTopicId === postData.topicId)
        : []
      const topicTitle = activeTopic.length ? activeTopic[0].title : ''
      props.postSuccess({
        posterImage: firstImage,
        topicId: postData.topicId || props.challengeId,
        topicTitle: topicTitle || postData.topicTitle || props.title,
        id: res.data.createId,
        popUpType: res.data?.popUpType || 0, // 1: 限时挑战投稿成功，并且在前xx名，0: 普通投稿成功
        title: postData.title,
        description: postData.description,
        createId: res.data.createId,
        wantable: wantable.value ? 1 : 0,
        type: res.data.contentType
      })
      emit('close')
      checkedListsRef.value?.resetMentionList()
      trackEvent.track('publish', {
        enter_method: tabType.value === 'loacl' ? 'upload' : props.fromWhere,
        page_name: pageName,
        challenge_id: topicId,
        content_type: originContentId ? 'shoutout_post' : 'post',
        media_type: postType.value,
        is_support_custom: showCustomizeBuyButton,
        is_creative_process_public: hidePrompt ? 0 : 1,
        ...(restoredFromDraft.value ? { content_source: 'draft' } : {}),
        target_post_id: originContentId,
        target_post_type: 'post'
      })
      localStorage.setItem('postHidePrompt', String(hidePrompt))
      return
    }
    $message &&
      $message({
        message: `${res.msg}`,
        type: 'error'
      })
  }

  const close = () => {
    if (relatedWorkPickerVisible.value) {
      closeRelatedWorkPicker()
      return
    }
    if (uploadLoading.value) {
      $message &&
        $message({
          type: 'error',
          title: '放弃发布吗？',
          message: '如果现在退出，你所编辑的内容将不会被保存。',
          timer: 0,
          config: {
            showClose: false,
            showIcon: false,
            showCancel: true,
            showConfirm: true
          },
          onConfirm() {
            emit('close')
          }
        })
      return
    }
    if (step.value === PostStep.SELECT_MEDIA && percent.value < 100 && percent.value > 1) {
      $message &&
        $message({
          type: 'error',
          title: '确定退出吗？',
          message: '你的创作内容将会保存在你个人资料的创作历史记录中。',
          timer: 0,
          config: {
            showClose: false,
            showIcon: false,
            showCancel: true,
            showConfirm: true
          },
          onConfirm() {
            emit('close')
          }
        })
      return
    }
    // 仅处于第二步（编辑页）时才弹出保存草稿弹窗
    if (step.value === PostStep.EDIT_POST) {
      confirmSaveDraft(() => {
        emit('close')
      })
    } else {
      emit('close')
    }
  }

  // 校验草稿保存二次确认弹窗
  const confirmSaveDraft = (onFinish: () => void, cancelText = '不保存返回') => {
    if (isDraftDisabled.value) {
      if (step.value === PostStep.EDIT_POST || !isExitContentEmpty()) {
        $message &&
          $message({
            type: 'error',
            title: '放弃发布吗？',
            message: '如果现在退出，你所编辑的内容将不会被保存。',
            timer: 0,
            config: {
              showClose: false,
              showIcon: false,
              showCancel: true,
              showConfirm: true
            },
            onConfirm() {
              onFinish()
            }
          })
      } else {
        onFinish()
      }
      return
    }

    if (isExitContentEmpty()) {
      onFinish()
      return
    }

    $message &&
      $message({
        type: 'error',
        title: '需要保留草稿？',
        message: '保存后可在草稿继续编辑，未保存的内容将丢失',
        timer: 0,
        config: {
          showClose: false,
          showIcon: false,
          showCancel: true,
          showConfirm: true,
          confirmText: '存草稿',
          cancelText
        },
        async onConfirm() {
          await saveAsDraft()
          ToastService({
            type: 'success',
            message: '保存成功'
          })
          emit('close')
        },
        async onCancel() {
          if (restoredFromDraft.value && currentDraftId.value) {
            await deleteDraft('POST', currentDraftId.value)
          }
          onFinish()
        }
      })
  }

  const clearAllPostSelections = () => {
    postData.checkedImages.clear()
    localImageList.value = []
    selectedRelatedWork.value = null
    postData.topicId = ''
    postData.topicTitle = ''
    resultRef.value && resultRef.value.clearChecked()
  }

  const unselect = () => {
    clearAllPostSelections()
  }

  const changeTab = (type: 'history' | 'loacl') => {
    if (type === tabType.value) return
    if (type === 'loacl') {
      trackEvent.track('click_upload_entrance')
    }
    if (postData.checkedImages.size > 0) {
      $message &&
        $message({
          type: 'error',
          title: '放弃所有已选内容',
          message: '你正在尝试切换模式，这将清除所有已选内容',
          timer: 0,
          config: {
            showClose: false,
            showIcon: false,
            showCancel: true,
            showConfirm: true
          },
          onConfirm() {
            unselect()
            tabType.value = type
          }
        })
    } else {
      tabType.value = type
    }
  }

  const challenges = ref<Challenge[]>([])
  const getChallenges = async () => {
    const response = await _fetch('/aigc/api/topic/selection', {
      method: 'GET',
      params: {
        page: 1,
        pageSize: 1000,
        showWantable: 1
      }
    })
    const res = await response.json()
    if (res.code === 200) {
      challenges.value = res.data.dataList
    }
  }

  const togglePromptSetting = () => {
    showPromptSetting.value = false
  }

  const wantedNum = ref(0)
  const selectHandle = ({
    topicId,
    title,
    equleNum,
    want,
    key,
    activityType,
    canPost
  }: SelectTopicParams) => {
    postData.topicId = topicId
    postData.topicTitle = title
    wantedNum.value = equleNum
    wantable.value = want === 1
    challengeId.value = topicId
    activitType.value = activityType
    if (canPost !== undefined) {
      checkCanPost.value = canPost
    }
    if (key) {
      postData.checkedImages.delete(key)
      uploadRef.value?.deleteImageByUrl(key)
    }
  }

  const getOriginImageInfo = (videoInfo: any) => {
    const sourcePath = getOriginImagePath(videoInfo)
    return {
      ...videoInfo.sourceImageInfo,
      target: 'image',
      uniqueTopicId: videoInfo.uniqueTopicId,
      imageResize512Url: sourcePath,
      url: sourcePath
    }
  }
  const getOriginImagePath = (videoInfo: any): string => {
    return videoInfo.sourceImageInfo?.itemId
      ? videoInfo.sourceImageInfo.sourceImagePath || ''
      : videoInfo.firstFrameUrl || ''
  }

  const disableBtn = computed(() => {
    if (isWantableLocalUpload.value) return false
    if (tabType.value === 'loacl') {
      return wantable.value && activitType.value === 1
    }

    if (postType.value === 'video') {
      return wantable.value && (postData.checkedImages.size > 2 || !checkCanPost.value)
    }
    return (
      wantable.value &&
      (wantedNum.value !== 1 || postData.checkedImages.size > 1 || !checkCanPost.value)
    )
  })

  // 本地上传列表：与 AppUploadLocalImage 内部列表始终是同一份数据（含仍在上传中的项）。
  // checkedImages 只是它「已上传完成」的投影，供提交与计数使用；
  // 不能反过来用 checkedImages 推导本列表，那样上传中的项会在回写时丢失。
  const localImageList = ref<ImageItem[]>([])

  const uploadImageChange = (images: ImageItem[]) => {
    localImageList.value = images
    postData.checkedImages.clear()
    images.forEach((item: ImageItem) => {
      // 只有上传完成的图片才进入选中列表
      if (!item.loading && item.url) {
        // 转换为 Image 类型。
        // 第二步的 CheckedLists 按 target 分流图片/视频、且缩略图直接取 imageResize512Url 无兜底，
        // 本地上传的图这两个字段都只能指望上传接口返回，这里统一补齐，避免被当成视频渲染。
        const imageData: any = {
          ...item,
          localKey: undefined,
          localUrl: undefined,
          target: (item as any).target || 'image',
          imageResize512Url: item.imageResize512Url || item.url
        }
        postData.checkedImages.set(item.url, imageData)
      }
    })
    uploadLoading.value = images.some((item: ImageItem) => item.loading)
  }

  // checkedImages 被本组件直接改动（草稿恢复、清空已选）后，回填本地上传列表用于回显
  const syncLocalImageListFromChecked = () => {
    localImageList.value = Array.from(postData.checkedImages.values()).map(
      (img: any, index: number) => ({
        ...img,
        imageResize512Url: img.imageResize512Url || img.url,
        localUrl: img.localUrl || img.url || '',
        localKey: img.localKey || img.url || `key_${index}`,
        delayIndex: index,
        loading: false
      })
    )
  }

  // 打开弹窗时刷新用户信息，确保 canPublishReferralContent 等权限字段是最新的
  const refreshUserInfo = async () => {
    try {
      const res: any = await getUserInfo()
      if (res?.code === 200 && res.data) {
        userStore.setUser(res.data)
      }
    } catch {
      // 刷新失败不影响发布流程，沿用现有 store 数据
    }
  }

  onMounted(async () => {
    lockScroll()
    getChallenges()
    initView()
    refreshUserInfo()

    const isFromPublishAction = props.scene === 'home'
    if (isFromPublishAction && !isDraftDisabled.value) {
      const draft = await getDraft<PostDraftPayload>('POST')
      if (draft && draft.payload) {
        trackEvent.track('popup_show', {
          page_name: 'publish_edit_page',
          popup_type: 'edit_draft',
          trigger_type: 'post_draft'
        })
        $message &&
          $message({
            type: 'error',
            title: '继续上次编辑？',
            message: '检测到您有未完成的草稿，是否继续编辑上次内容？',
            timer: 0,
            config: {
              showClose: false,
              showIcon: false,
              showCancel: true,
              showConfirm: true,
              confirmText: '继续编辑',
              cancelText: '放弃'
            },
            onConfirm() {
              trackEvent.track('popup_click', {
                page_name: 'publish_edit_page',
                popup_type: 'edit_draft',
                button_type: 'continue',
                trigger_type: 'post_draft'
              })
              const payload = draft.payload as any
              currentDraftId.value = (draft as any).draftId || payload.draftId
              currentDraftVersion.value = draft.version
              if (payload.tabType) {
                tabType.value = payload.tabType
              }
              if (payload.historyTabType) {
                historyTabType.value = payload.historyTabType
              }
              if (payload.title !== undefined) postData.title = payload.title
              if (payload.description !== undefined) postData.description = payload.description

              const topicId = payload.selectedTopic?.uniqueTopicId
              const topicTitle = payload.selectedTopic?.name || payload.selectedTopic?.title
              if (topicId) postData.topicId = topicId
              if (topicTitle) postData.topicTitle = topicTitle

              if (payload.originalImage !== undefined) {
                postData.associateOriginalImage = payload.originalImage
                checkedListsRef.value?.setAssociateOriginalImage(payload.originalImage)
              }

              if (payload.selectedRelatedWork) {
                selectedRelatedWork.value = payload.selectedRelatedWork
                const targetId = payload.selectedRelatedWork.uniqueContentId
                if (targetId) {
                  http
                    .get(
                      '/aigc/api/topic/content/detail',
                      { contentId: targetId, showErrorIfNotFound: 1 },
                      { isShowErrorModal: false }
                    )
                    .then((res: any) => {
                      if (!res || (typeof res.code === 'number' && res.code !== 200)) {
                        ToastService({
                          message: '关联的帖子已经删除',
                          type: 'error',
                          duration: 1500
                        })
                        selectedRelatedWork.value = null
                      } else {
                        const detail = res?.data ?? res
                        if (detail?.wantable !== 1) {
                          ToastService({
                            message: '关联贴已失效，请重新关联',
                            type: 'error',
                            duration: 1500
                          })
                          selectedRelatedWork.value = null
                        }
                      }
                    })
                    .catch((err: any) => {
                      if (err?.code && typeof err.code === 'number' && err.code !== 200) {
                        ToastService({
                          message: '关联的帖子已经删除',
                          type: 'error',
                          duration: 1500
                        })
                        selectedRelatedWork.value = null
                      }
                    })
                }
              }

              if (payload.mediaList && payload.mediaList.length > 0) {
                postData.checkedImages.clear()
                payload.mediaList.forEach((img: any) => {
                  const key = img.url || getOriginImagePath(img)
                  if (key) {
                    postData.checkedImages.set(key, img)
                  }
                })
                syncLocalImageListFromChecked()
                step.value = PostStep.EDIT_POST
              } else if (
                payload.title ||
                payload.description ||
                topicId ||
                payload.selectedRelatedWork
              ) {
                step.value = PostStep.EDIT_POST
              }
              if (payload.mentionList && payload.mentionList.length > 0) {
                checkedListsRef.value?.setMentionList(payload.mentionList)
              }
              if (typeof payload.enablePurchase === 'boolean') {
                checkedListsRef.value?.setEnablePurchase(payload.enablePurchase)
              }
              if (typeof payload.originalImage === 'boolean') {
                checkedListsRef.value?.setAssociateOriginalImage(payload.originalImage)
              }
              restoredFromDraft.value = true
              trackEvent.track('publish_edit_page', {
                page_name: 'post_page',
                content_source: 'draft'
              })
            }
          })
      }
    }
  })

  const onChecked = (obj: { item: any; checked: boolean }) => {
    const { item, checked } = obj
    if (checked) {
      // 如果选中的是一个视频，并且当前没有数据源，是一个切换Tab的场景，需要从新赋值postData.checkedImages信息
      if (item.target === 'video') {
        if (postData.checkedImages.size === 0) {
          const sourcePath = getOriginImagePath(item)
          postData.checkedImages.set(item.url, item)
          postData.checkedImages.set(sourcePath, getOriginImageInfo(item))
        }
      } else {
        postData.checkedImages.set(item.url, item)
      }
    } else {
      postData.checkedImages.delete(item?.url ?? '')
      // 如果是视频还要把原图数据删除
      if (item.target === 'video') {
        postData.checkedImages.delete(item.sourceImageInfo.sourceImagePath ?? '')
      }
    }
  }

  // 必须等原图信息赋值后，再根据是否关联原图配置，增删数据源
  watch(
    [() => postData.associateOriginalImage],
    () => {
      if (postType.value !== 'video') {
        return
      }
      const videoInfo = Array.from(postData.checkedImages.values())[0]
      if (!videoInfo) {
        return
      }
      const sourcePath = getOriginImagePath(videoInfo)
      //根据是否关联原图配置，增删数据源
      if (postData.associateOriginalImage && postData.checkedImages.size === 1) {
        // 显示原图
        const formatSourceInfo = getOriginImageInfo(videoInfo)
        postData.checkedImages.set(sourcePath, formatSourceInfo)
      } else if (!postData.associateOriginalImage && postData.checkedImages.size === 2) {
        // 删除原图
        postData.checkedImages.delete(sourcePath)
      }
    },
    {
      immediate: true
    }
  )

  const clickUnlock = () => {
    let origin = window.location.origin
    window.location.href = origin + '/author/onboarding'
  }
  const nextStepEvent = () => {
    if (uploadLoading.value) {
      return
    }
    if (postData.checkedImages.size) {
      step.value = PostStep.EDIT_POST
    }
  }

  onBeforeUnmount(() => {
    unlockScroll()
  })

  // 关联原图和隐藏描述词设置变更触发此函数
  const backConfigSettings = (configs: ConfigSettings) => {
    postData.associateOriginalImage = configs.associateOriginalImage
  }
</script>
<template>
  <main class="detail-mask-container">
    <div
      class="detail-container"
      :class="{
        'post-detail-container': step === PostStep.EDIT_POST,
        'related-work-picker-container': relatedWorkPickerVisible
      }"
      id="post_dialog"
      @click="
        event => {
          event.stopPropagation()
          togglePromptSetting()
        }
      "
    >
      <a class="detail-close" @click.stop="close">
        <span class="hitems-close"></span>
      </a>

      <div class="post-content">
        <div v-show="step === PostStep.SELECT_MEDIA">
          <div class="post-tabs">
            <a
              v-if="hasHistory"
              :class="['post-tab', 'font-bold', { active: tabType === 'history' }]"
              @click="changeTab('history')"
            >
              历史作品发布
            </a>
            <a
              :class="['post-tab', 'font-bold', { active: tabType === 'loacl' }]"
              @click="changeTab('loacl')"
            >
              <span
                :class="isPGCUser ? 'hitems-jiesuo' : 'hitems-ic_lock'"
                v-if="
                  (props.scene === 'home' || props.scene === 'normalChallenge') &&
                  !isWantableLocalUpload
                "
              ></span>
              本地上传并发布
            </a>
          </div>

          <div v-if="tabType === 'history'">
            <GeneratorHistory
              v-model="postData.checkedImages"
              v-model:curTab="historyTabType"
              :checkedLength="postData.checkedImages.size"
              :topicId="postData.topicId"
              :wantable="wantable || false"
              @checked="
                data => {
                  onChecked(data)
                }
              "
              @clearCheckedItems="clearAllPostSelections"
            />
            <div class="post-footer">
              <div></div>
              <a
                :class="[
                  'post-btn',
                  'post-btn-submit',
                  'font-bold',
                  { disabled: !postData.checkedImages.size }
                ]"
                @click="nextStep"
              >
                确定
              </a>
            </div>
          </div>

          <div v-if="tabType === 'loacl'">
            <UpdataLocalImage
              v-if="showLocalUpload"
              ref="uploadRef"
              v-model="localImageList"
              :maxCount="isWantableLocalUpload ? 1 : 9"
              :maxSize="20"
              accept="image/jpeg,image/png,image/jpg,image/webp"
              :disabled="false"
              uploadUrl="/aigc/api/generate/generate-image"
              :topicId="isWantableLocalUpload ? challengeId : ''"
              @changeEvent="uploadImageChange"
            />
            <div class="creator-banner-img-wrapper" v-else>
              <div class="creator-banner-img-box">
                <AppImage :src="creatorBannerImg" alt="" />
                <div class="creator-banner-img-btn">
                  <a :class="['post-btn', 'post-btn-submit', 'font-bold']" @click="clickUnlock">
                    立刻入驻
                  </a>
                </div>
              </div>
            </div>

            <div class="post-footer" v-if="showLocalUpload">
              <div class="post-footer-text">
                <p v-if="postData.checkedImages.size > 0">
                  图片支持 JPG/PNG/JPEG/WEBP，需小于 20 MB
                  <template v-if="!isWantableLocalUpload">
                    ，已上传（{{ postData.checkedImages.size }}/9）
                  </template>
                </p>
              </div>
              <a
                :class="[
                  'post-btn',
                  'post-btn-submit',
                  'font-bold',
                  { disabled: !postData.checkedImages.size || uploadLoading }
                ]"
                @click="nextStepEvent"
              >
                确定
              </a>
            </div>
          </div>
        </div>

        <CheckedLists
          v-show="step === PostStep.EDIT_POST && !relatedWorkPickerVisible"
          ref="checkedListsRef"
          v-model="postData.checkedImages"
          v-model:related-work="selectedRelatedWork"
          :challenges="challenges"
          :title="props.title"
          :titleMsg="postData.title"
          :descriptionMsg="postData.description"
          :topicIdMsg="postData.topicId || props.challengeId"
          :tabType="tabType"
          :isShow="step === PostStep.EDIT_POST"
          :hasNotBack="props.checkedImages && props.checkedImages.length > 0"
          :isWantableLocalUpload="isWantableLocalUpload"
          :is-related-work-enabled="isRelatedWorkEnabled"
          @input="onInput"
          @select="selectHandle"
          @checkedMore="onCheckedMore"
          @back="onBack"
          @backConfigSettings="backConfigSettings"
          @open-related-work-picker="openRelatedWorkPicker"
        />

        <RelatedWorkSelectPanel
          v-if="step === PostStep.EDIT_POST && relatedWorkPickerVisible"
          :selected-work="selectedRelatedWork"
          @close="closeRelatedWorkPicker"
          @confirm="handleRelatedWorkConfirm"
          @header-tip-press="relatedWorkRuleTipVisible = true"
        />
      </div>

      <RelatedWorkSelectTipPopup
        :visible="relatedWorkRuleTipVisible"
        @close="handleRelatedWorkRuleTipClose"
      />

      <div class="post-footer" v-if="step === PostStep.EDIT_POST && !relatedWorkPickerVisible">
        <div></div>
        <span class="post-btn" v-if="suiting">
          <AppLoading />
        </span>
        <a
          v-else
          :class="[
            'post-btn',
            'post-btn-submit',
            'font-bold',
            {
              disabled: disableBtn
            }
          ]"
          @click="postPreCheck"
        >
          发布
          <div
            v-if="tabType !== 'loacl' && postData.topicId && wantedNum === 0 && wantable"
            class="prompt prompt1"
          >
            <img class="union" src="@/assets/images/Union.png" />
            <span>请添加一张符合「想要」挑战主题的图片</span>
          </div>
          <div
            v-if="
              tabType !== 'loacl' &&
              postType !== 'video' &&
              postData.topicId &&
              wantedNum > 1 &&
              wantable
            "
            class="prompt"
          >
            <img class="union" src="@/assets/images/Union.png" />
            <span>「想要」挑战只支持发布1张图片</span>
          </div>
        </a>
      </div>
    </div>
  </main>
</template>
<style lang="stylus" scoped>
  @import './AppPostChallenge.styl';
</style>
